import React, { useState, useEffect, useCallback } from 'react';
import {
  Loader, AlertCircle, Copy, Check, TrendingUp, Users, Wallet,
  BadgePercent, ExternalLink, RefreshCw, Sparkles, ArrowRight, Clock,
  Mail, Send, X
} from 'lucide-react';
import { apiCallJson } from '../utils/api';
import { useTheme } from '../context/ThemeContext';
import { useToast } from './Toast';

/**
 * Affiliate Dashboard
 * -------------------
 * Single-page view for any user with an `affiliates` row. Renders different
 * shells per status:
 *   pending  → "we're reviewing your application"
 *   rejected → "your application was not approved" + reason
 *   paused   → banner + still show stats
 *   approved → full dashboard
 *
 * Pure-affiliate cross-sell banner is rendered when `showCrossSell` is true,
 * which the parent decides based on whether the user has any Google Ads
 * connection.
 */

const STATUS_PILL = {
  pending:  { label: 'Application pending', color: '#f59e0b' },
  approved: { label: 'Approved',            color: '#10b981' },
  rejected: { label: 'Rejected',            color: '#ef4444' },
  paused:   { label: 'Paused',              color: '#6b7280' }
};

const REFERRAL_STATUS = {
  pending:  { label: 'Signed up',    color: '#3b82f6' },
  paying:   { label: 'Paying',       color: '#10b981' },
  refunded: { label: 'Refunded',     color: '#ef4444' },
  churned:  { label: 'Churned',      color: '#6b7280' }
};

const COMMISSION_STATUS = {
  earned:   { label: 'Earned',   color: '#10b981' },
  reversed: { label: 'Reversed', color: '#ef4444' },
  paid:     { label: 'Paid out', color: '#3b82f6' }
};

const PAYOUT_STATUS = {
  pending:  { label: 'Pending',  color: '#f59e0b' },
  exported: { label: 'Exported', color: '#3b82f6' },
  paid:     { label: 'Paid',     color: '#10b981' },
  failed:   { label: 'Failed',   color: '#ef4444' }
};

function formatEuros(cents) {
  if (cents === null || cents === undefined) return '—';
  const negative = cents < 0;
  const value = (Math.abs(cents) / 100).toLocaleString('en-US', {
    minimumFractionDigits: 2, maximumFractionDigits: 2
  });
  return `${negative ? '−' : ''}€${value}`;
}

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function Pill({ cfg }) {
  if (!cfg) return null;
  return (
    <span style={{
      display: 'inline-block',
      padding: '3px 9px',
      borderRadius: 6,
      fontSize: 11,
      fontWeight: 600,
      background: `${cfg.color}1f`,
      color: cfg.color,
      textTransform: 'uppercase',
      letterSpacing: '0.04em'
    }}>{cfg.label}</span>
  );
}

function StatCard({ icon: Icon, label, value, sub, theme, accent = '#10b981' }) {
  return (
    <div style={{
      background: theme.colors.bg.secondary,
      border: `1px solid ${theme.colors.border.primary}`,
      borderRadius: 12,
      padding: 18,
      display: 'flex',
      gap: 14,
      alignItems: 'flex-start'
    }}>
      <div style={{
        width: 36, height: 36, borderRadius: 10,
        background: `${accent}1f`, color: accent,
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
      }}>
        <Icon size={18} />
      </div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ color: theme.colors.text.muted, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
          {label}
        </div>
        <div style={{ color: theme.colors.text.primary, fontSize: 22, fontWeight: 700, lineHeight: 1.1 }}>
          {value}
        </div>
        {sub && (
          <div style={{ color: theme.colors.text.tertiary, fontSize: 12, marginTop: 4 }}>
            {sub}
          </div>
        )}
      </div>
    </div>
  );
}

function PayoutProgress({ pendingCents, minPayoutCents, theme }) {
  const pct = Math.min(100, Math.max(0, (pendingCents / minPayoutCents) * 100));
  const reached = pendingCents >= minPayoutCents;

  return (
    <div style={{
      background: theme.colors.bg.secondary,
      border: `1px solid ${theme.colors.border.primary}`,
      borderRadius: 12,
      padding: 18
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div>
          <div style={{ color: theme.colors.text.muted, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
            Pending payout
          </div>
          <div style={{ color: theme.colors.text.primary, fontSize: 26, fontWeight: 700 }}>
            {formatEuros(pendingCents)}
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ color: theme.colors.text.muted, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
            Minimum
          </div>
          <div style={{ color: theme.colors.text.secondary, fontSize: 16, fontWeight: 600 }}>
            {formatEuros(minPayoutCents)}
          </div>
        </div>
      </div>
      <div style={{
        height: 8, borderRadius: 999,
        background: theme.colors.bg.tertiary, overflow: 'hidden'
      }}>
        <div style={{
          width: `${pct}%`, height: '100%',
          background: reached
            ? 'linear-gradient(90deg, #10b981, #34d399)'
            : 'linear-gradient(90deg, #6366f1, #8b5cf6)',
          transition: 'width 0.4s ease'
        }} />
      </div>
      <div style={{ marginTop: 10, fontSize: 12, color: theme.colors.text.tertiary, lineHeight: 1.5 }}>
        {reached
          ? <>You're above the payout threshold. The next monthly batch will include your balance.</>
          : <>You need {formatEuros(Math.max(0, minPayoutCents - pendingCents))} more before the next monthly payout includes your balance.</>}
      </div>
    </div>
  );
}

function ShareLinkCard({ shareLink, refCode, commissionPct, cookieDays, theme, toast }) {
  const [copied, setCopied] = useState(false);

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      setCopied(true);
      toast?.success('Referral link copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      toast?.error('Could not copy link, please copy it manually');
    }
  };

  return (
    <div style={{
      background: `linear-gradient(135deg, rgba(99,102,241,0.18) 0%, rgba(139,92,246,0.10) 100%)`,
      border: `1px solid rgba(99,102,241,0.35)`,
      borderRadius: 12,
      padding: 18
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <Sparkles size={16} color="#8b5cf6" />
        <div style={{ color: theme.colors.text.primary, fontSize: 13, fontWeight: 700 }}>
          Your referral link
        </div>
      </div>

      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        background: theme.colors.bg.primary,
        border: `1px solid ${theme.colors.border.primary}`,
        borderRadius: 8,
        padding: '10px 12px',
        marginBottom: 10,
        overflow: 'hidden'
      }}>
        <code style={{
          flex: 1,
          fontSize: 13,
          fontFamily: 'monospace',
          color: theme.colors.text.primary,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }}>{shareLink}</code>
        <button
          onClick={onCopy}
          style={{
            background: copied ? '#10b981' : '#6366f1',
            color: 'white',
            border: 'none',
            borderRadius: 6,
            padding: '6px 12px',
            fontSize: 12,
            fontWeight: 600,
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            flexShrink: 0
          }}
        >
          {copied ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy</>}
        </button>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, fontSize: 12, color: theme.colors.text.secondary }}>
        <div><strong style={{ color: theme.colors.text.primary }}>{commissionPct}%</strong> commission per paid invoice</div>
        <div>·</div>
        <div><strong style={{ color: theme.colors.text.primary }}>{cookieDays}-day</strong> cookie window</div>
        <div>·</div>
        <div>Your code: <strong style={{ color: theme.colors.text.primary, fontFamily: 'monospace' }}>{refCode}</strong></div>
      </div>
    </div>
  );
}

function CrossSellBanner({ theme }) {
  return (
    <div style={{
      background: `linear-gradient(135deg, rgba(16,185,129,0.15) 0%, rgba(59,130,246,0.10) 100%)`,
      border: `1px solid rgba(16,185,129,0.3)`,
      borderRadius: 12,
      padding: 16,
      marginBottom: 16,
      display: 'flex',
      alignItems: 'center',
      gap: 14
    }}>
      <div style={{
        width: 40, height: 40, borderRadius: 10,
        background: '#10b9811f', color: '#10b981',
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
      }}>
        <Sparkles size={20} />
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ color: theme.colors.text.primary, fontSize: 14, fontWeight: 700, marginBottom: 2 }}>
          Try Bidoryx as a customer too
        </div>
        <div style={{ color: theme.colors.text.secondary, fontSize: 13, lineHeight: 1.5 }}>
          Connect your own Google Ads account to see what your referrals get. Personal experience makes for the best recommendations.
        </div>
      </div>
      <a
        href="/connect-google-ads"
        style={{
          background: '#10b981',
          color: 'white',
          padding: '8px 14px',
          borderRadius: 8,
          fontSize: 13,
          fontWeight: 600,
          textDecoration: 'none',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          flexShrink: 0
        }}
      >
        Connect <ArrowRight size={14} />
      </a>
    </div>
  );
}

function ReferralsTable({ rows, theme }) {
  if (!rows.length) {
    return (
      <div style={{ color: theme.colors.text.tertiary, fontSize: 13, padding: 24, textAlign: 'center' }}>
        No referrals yet. Share your link to start earning.
      </div>
    );
  }
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ borderBottom: `1px solid ${theme.colors.border.primary}` }}>
            <th style={{ textAlign: 'left', padding: '10px 12px', color: theme.colors.text.muted, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Customer</th>
            <th style={{ textAlign: 'left', padding: '10px 12px', color: theme.colors.text.muted, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Signed up</th>
            <th style={{ textAlign: 'left', padding: '10px 12px', color: theme.colors.text.muted, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>First payment</th>
            <th style={{ textAlign: 'left', padding: '10px 12px', color: theme.colors.text.muted, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.id} style={{ borderBottom: `1px solid ${theme.colors.border.primary}` }}>
              <td style={{ padding: '10px 12px', color: theme.colors.text.primary }}>
                {r.referred_name || r.referred_email || <span style={{ color: theme.colors.text.tertiary }}>(deleted user)</span>}
              </td>
              <td style={{ padding: '10px 12px', color: theme.colors.text.secondary }}>{formatDate(r.attributed_at)}</td>
              <td style={{ padding: '10px 12px', color: theme.colors.text.secondary }}>{formatDate(r.first_payment_at)}</td>
              <td style={{ padding: '10px 12px' }}><Pill cfg={REFERRAL_STATUS[r.status]} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CommissionsTable({ rows, theme }) {
  if (!rows.length) {
    return (
      <div style={{ color: theme.colors.text.tertiary, fontSize: 13, padding: 24, textAlign: 'center' }}>
        No commissions yet. Once a referred customer pays an invoice you'll see entries here.
      </div>
    );
  }
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ borderBottom: `1px solid ${theme.colors.border.primary}` }}>
            <th style={{ textAlign: 'left', padding: '10px 12px', color: theme.colors.text.muted, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Date</th>
            <th style={{ textAlign: 'left', padding: '10px 12px', color: theme.colors.text.muted, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Customer</th>
            <th style={{ textAlign: 'right', padding: '10px 12px', color: theme.colors.text.muted, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Net invoice</th>
            <th style={{ textAlign: 'right', padding: '10px 12px', color: theme.colors.text.muted, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Commission</th>
            <th style={{ textAlign: 'left', padding: '10px 12px', color: theme.colors.text.muted, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(c => {
            const isNeg = parseInt(c.commission_cents) < 0;
            return (
              <tr key={c.id} style={{ borderBottom: `1px solid ${theme.colors.border.primary}` }}>
                <td style={{ padding: '10px 12px', color: theme.colors.text.secondary }}>{formatDate(c.earned_at)}</td>
                <td style={{ padding: '10px 12px', color: theme.colors.text.primary }}>
                  {c.referred_name || c.referred_email || <span style={{ color: theme.colors.text.tertiary }}>—</span>}
                </td>
                <td style={{ padding: '10px 12px', color: theme.colors.text.secondary, textAlign: 'right' }}>{formatEuros(c.invoice_amount_cents)}</td>
                <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600,
                  color: isNeg ? '#ef4444' : '#10b981'
                }}>{formatEuros(c.commission_cents)}</td>
                <td style={{ padding: '10px 12px' }}><Pill cfg={COMMISSION_STATUS[c.status]} /></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function PayoutsTable({ rows, theme }) {
  if (!rows.length) {
    return (
      <div style={{ color: theme.colors.text.tertiary, fontSize: 13, padding: 24, textAlign: 'center' }}>
        No payouts yet. Payouts are processed monthly via SEPA bank transfer once your balance reaches the minimum.
      </div>
    );
  }
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ borderBottom: `1px solid ${theme.colors.border.primary}` }}>
            <th style={{ textAlign: 'left', padding: '10px 12px', color: theme.colors.text.muted, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Period</th>
            <th style={{ textAlign: 'right', padding: '10px 12px', color: theme.colors.text.muted, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Amount</th>
            <th style={{ textAlign: 'left', padding: '10px 12px', color: theme.colors.text.muted, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Status</th>
            <th style={{ textAlign: 'left', padding: '10px 12px', color: theme.colors.text.muted, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Paid on</th>
            <th style={{ textAlign: 'left', padding: '10px 12px', color: theme.colors.text.muted, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Reference</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(p => (
            <tr key={p.id} style={{ borderBottom: `1px solid ${theme.colors.border.primary}` }}>
              <td style={{ padding: '10px 12px', color: theme.colors.text.primary }}>
                {formatDate(p.period_start)} – {formatDate(p.period_end)}
              </td>
              <td style={{ padding: '10px 12px', color: theme.colors.text.primary, fontWeight: 600, textAlign: 'right' }}>
                {formatEuros(p.amount_cents)}
              </td>
              <td style={{ padding: '10px 12px' }}><Pill cfg={PAYOUT_STATUS[p.status]} /></td>
              <td style={{ padding: '10px 12px', color: theme.colors.text.secondary }}>{formatDate(p.paid_at)}</td>
              <td style={{ padding: '10px 12px', color: theme.colors.text.tertiary, fontSize: 12, fontFamily: 'monospace' }}>
                {p.reference || '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ShareInviteCard({ stats, dailyLimit, onSend, theme, toast, disabled }) {
  const [recipientEmail, setRecipientEmail] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const remaining = Math.max(0, (dailyLimit || 25) - (stats?.sent24h || 0));
  const isAtLimit = remaining === 0;

  const inputStyle = {
    width: '100%',
    padding: '9px 12px',
    background: theme.colors.bg.tertiary,
    color: theme.colors.text.primary,
    border: `1px solid ${theme.colors.border.primary}`,
    borderRadius: 8,
    fontSize: 13,
    boxSizing: 'border-box',
    fontFamily: 'inherit'
  };
  const labelStyle = {
    display: 'block',
    color: theme.colors.text.secondary,
    fontSize: 12,
    fontWeight: 600,
    marginBottom: 4
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!recipientEmail.trim()) {
      toast?.error('Please enter an email address');
      return;
    }
    if (isAtLimit) {
      toast?.error(`Daily limit of ${dailyLimit} reached — try again tomorrow.`);
      return;
    }
    setSubmitting(true);
    try {
      const result = await onSend({
        recipientEmail: recipientEmail.trim(),
        recipientName: recipientName.trim() || null,
        message: message.trim() || null
      });
      if (!result?.success) {
        toast?.error(result?.error || 'Could not send invitation');
        return;
      }
      if (result.emailDelivered) {
        toast?.success(`Invitation sent to ${recipientEmail.trim()}`);
      } else {
        toast?.warning(
          `Logged the invite, but the email could not be delivered. ${result.error || ''}`
        );
      }
      setRecipientEmail('');
      setRecipientName('');
      setMessage('');
    } catch (err) {
      toast?.error(err.message || 'Could not send invitation');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{
      background: theme.colors.bg.secondary,
      border: `1px solid ${theme.colors.border.primary}`,
      borderRadius: 12,
      padding: 18
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 14 }}>
        <div style={{
          width: 36, height: 36, borderRadius: 10,
          background: '#3b82f61f', color: '#3b82f6',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
        }}>
          <Mail size={18} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ color: theme.colors.text.primary, fontSize: 15, fontWeight: 700, marginBottom: 2 }}>
            Email a prospect your referral link
          </div>
          <div style={{ color: theme.colors.text.tertiary, fontSize: 12 }}>
            We send a Bidoryx-branded email with your personal referral link. Replies go straight to you.
          </div>
        </div>
        <div style={{
          textAlign: 'right',
          color: isAtLimit ? '#ef4444' : theme.colors.text.tertiary,
          fontSize: 11, fontWeight: 600,
          flexShrink: 0
        }}>
          {remaining}/{dailyLimit || 25}
          <div style={{ color: theme.colors.text.muted, fontWeight: 500, marginTop: 2 }}>left today</div>
        </div>
      </div>

      <form onSubmit={submit} style={{ display: 'grid', gap: 10 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div>
            <label style={labelStyle}>Email <span style={{ color: '#ef4444' }}>*</span></label>
            <input
              type="email"
              required
              value={recipientEmail}
              onChange={(e) => setRecipientEmail(e.target.value)}
              placeholder="prospect@example.com"
              disabled={submitting || disabled}
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>Name (optional)</label>
            <input
              type="text"
              value={recipientName}
              onChange={(e) => setRecipientName(e.target.value)}
              placeholder="Jane Doe"
              disabled={submitting || disabled}
              style={inputStyle}
            />
          </div>
        </div>
        <div>
          <label style={labelStyle}>Personal note (optional)</label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Hey Jane — I think Bidoryx could really help your search campaigns. Worth a 5-minute look."
            rows={3}
            maxLength={1000}
            disabled={submitting || disabled}
            style={{ ...inputStyle, resize: 'vertical', minHeight: 60 }}
          />
          <div style={{ color: theme.colors.text.muted, fontSize: 11, marginTop: 4, textAlign: 'right' }}>
            {message.length}/1000
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            type="submit"
            disabled={submitting || disabled || isAtLimit}
            style={{
              background: (submitting || disabled || isAtLimit) ? theme.colors.bg.tertiary : '#3b82f6',
              color: (submitting || disabled || isAtLimit) ? theme.colors.text.tertiary : '#fff',
              border: 'none',
              borderRadius: 8,
              padding: '9px 18px',
              fontSize: 13,
              fontWeight: 600,
              cursor: (submitting || disabled || isAtLimit) ? 'not-allowed' : 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: 6
            }}
          >
            {submitting
              ? <><Loader size={14} className="spin" /> Sending…</>
              : <><Send size={14} /> Send invitation</>
            }
          </button>
        </div>
      </form>
    </div>
  );
}

function ShareInvitesTable({ rows, theme }) {
  if (!rows || rows.length === 0) {
    return (
      <div style={{ padding: 24, textAlign: 'center', color: theme.colors.text.tertiary, fontSize: 13 }}>
        No invitations sent yet — use the form above to invite a prospect.
      </div>
    );
  }
  const tdStyle = { padding: '10px 12px', fontSize: 12, color: theme.colors.text.primary };
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ background: theme.colors.bg.tertiary }}>
            <th style={{ ...tdStyle, fontWeight: 600, color: theme.colors.text.secondary, textAlign: 'left' }}>Recipient</th>
            <th style={{ ...tdStyle, fontWeight: 600, color: theme.colors.text.secondary, textAlign: 'left' }}>Sent</th>
            <th style={{ ...tdStyle, fontWeight: 600, color: theme.colors.text.secondary, textAlign: 'left' }}>Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(row => (
            <tr key={row.id} style={{ borderTop: `1px solid ${theme.colors.border.primary}` }}>
              <td style={tdStyle}>
                <div style={{ fontWeight: 600 }}>{row.recipientEmail}</div>
                {row.recipientName && (
                  <div style={{ color: theme.colors.text.tertiary, fontSize: 11 }}>{row.recipientName}</div>
                )}
              </td>
              <td style={tdStyle}>{formatDate(row.createdAt)}</td>
              <td style={tdStyle}>
                {row.emailDelivered ? (
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    color: '#10b981', fontSize: 11, fontWeight: 600
                  }}>
                    <Check size={12} /> Delivered
                  </span>
                ) : (
                  <span
                    title={row.emailError || ''}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      color: '#ef4444', fontSize: 11, fontWeight: 600
                    }}
                  >
                    <X size={12} /> Not delivered
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Section({ title, theme, children, right = null }) {
  return (
    <div style={{
      background: theme.colors.bg.secondary,
      border: `1px solid ${theme.colors.border.primary}`,
      borderRadius: 12,
      marginBottom: 16,
      overflow: 'hidden'
    }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '14px 18px',
        borderBottom: `1px solid ${theme.colors.border.primary}`
      }}>
        <div style={{ color: theme.colors.text.primary, fontSize: 14, fontWeight: 700 }}>{title}</div>
        {right}
      </div>
      <div>{children}</div>
    </div>
  );
}

export default function AffiliateDashboard({ showCrossSell = false }) {
  const { theme } = useTheme();
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiCallJson('/api/affiliate/dashboard');
      if (!res?.success) throw new Error(res?.error || 'Failed to load affiliate dashboard');
      setData(res);
      return res;
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div style={{ padding: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, color: theme.colors.text.secondary }}>
        <Loader size={18} className="spin" /> Loading affiliate dashboard…
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        margin: 24, padding: 18,
        background: theme.colors.bg.secondary,
        border: `1px solid ${theme.colors.border.primary}`,
        borderRadius: 12,
        display: 'flex', alignItems: 'center', gap: 12
      }}>
        <AlertCircle size={20} color="#ef4444" />
        <div style={{ flex: 1, color: theme.colors.text.primary }}>{error}</div>
        <button
          onClick={load}
          style={{
            background: theme.colors.bg.tertiary, color: theme.colors.text.primary,
            border: `1px solid ${theme.colors.border.primary}`, borderRadius: 8,
            padding: '6px 12px', cursor: 'pointer', fontSize: 13
          }}
        >
          <RefreshCw size={14} style={{ verticalAlign: 'middle' }} /> Retry
        </button>
      </div>
    );
  }

  // No affiliate row at all → invite user to sign up.
  if (!data?.affiliate) {
    return (
      <div style={{ padding: 32, maxWidth: 720, margin: '0 auto' }}>
        <div style={{
          background: theme.colors.bg.secondary,
          border: `1px solid ${theme.colors.border.primary}`,
          borderRadius: 12,
          padding: 28,
          textAlign: 'center'
        }}>
          <Sparkles size={40} color="#8b5cf6" style={{ marginBottom: 12 }} />
          <h2 style={{ color: theme.colors.text.primary, fontSize: 22, fontWeight: 700, marginBottom: 10 }}>
            Become a Bidoryx affiliate
          </h2>
          <p style={{ color: theme.colors.text.secondary, fontSize: 14, lineHeight: 1.6, maxWidth: 540, margin: '0 auto 18px' }}>
            Earn {data?.defaults?.commissionPct || 22}% recurring commission for every paying customer you refer.
            Apply to join the program — we'll review your application and get back to you.
          </p>
          <a
            href="/affiliate/signup"
            style={{
              background: '#6366f1', color: 'white',
              padding: '10px 20px', borderRadius: 8,
              fontSize: 14, fontWeight: 600, textDecoration: 'none',
              display: 'inline-flex', alignItems: 'center', gap: 8
            }}
          >
            Apply to join <ArrowRight size={16} />
          </a>
        </div>
      </div>
    );
  }

  const a = data.affiliate;
  const isApproved = a.status === 'approved';
  const isPending  = a.status === 'pending';
  const isRejected = a.status === 'rejected';
  const isPaused   = a.status === 'paused';

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18 }}>
        <h1 style={{ color: theme.colors.text.primary, fontSize: 22, fontWeight: 700, margin: 0 }}>
          Affiliate Program
        </h1>
        <Pill cfg={STATUS_PILL[a.status]} />
        <div style={{ flex: 1 }} />
        <button
          onClick={load}
          style={{
            background: theme.colors.bg.secondary, color: theme.colors.text.secondary,
            border: `1px solid ${theme.colors.border.primary}`, borderRadius: 8,
            padding: '6px 12px', cursor: 'pointer', fontSize: 13,
            display: 'inline-flex', alignItems: 'center', gap: 6
          }}
        >
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {showCrossSell && <CrossSellBanner theme={theme} />}

      {isPending && (
        <div style={{
          background: 'rgba(245,158,11,0.10)',
          border: '1px solid rgba(245,158,11,0.35)',
          borderRadius: 12, padding: 18, marginBottom: 16,
          display: 'flex', gap: 12, alignItems: 'flex-start'
        }}>
          <Clock size={20} color="#f59e0b" style={{ marginTop: 2 }} />
          <div>
            <div style={{ color: theme.colors.text.primary, fontSize: 14, fontWeight: 700, marginBottom: 4 }}>
              Your application is being reviewed
            </div>
            <div style={{ color: theme.colors.text.secondary, fontSize: 13, lineHeight: 1.5 }}>
              We typically respond within 1–2 business days. Once approved, you'll see your unique referral link and stats here.
            </div>
          </div>
        </div>
      )}

      {isRejected && (
        <div style={{
          background: 'rgba(239,68,68,0.10)',
          border: '1px solid rgba(239,68,68,0.35)',
          borderRadius: 12, padding: 18, marginBottom: 16
        }}>
          <div style={{ color: theme.colors.text.primary, fontSize: 14, fontWeight: 700, marginBottom: 4 }}>
            Your application was not approved
          </div>
          {a.rejectedReason && (
            <div style={{ color: theme.colors.text.secondary, fontSize: 13, lineHeight: 1.5 }}>
              Reason: {a.rejectedReason}
            </div>
          )}
        </div>
      )}

      {isPaused && (
        <div style={{
          background: 'rgba(107,114,128,0.10)',
          border: '1px solid rgba(107,114,128,0.35)',
          borderRadius: 12, padding: 14, marginBottom: 16,
          color: theme.colors.text.secondary, fontSize: 13
        }}>
          Your affiliate account is currently paused. Existing commissions remain visible, but new referrals are not being attributed. Contact support to reactivate.
        </div>
      )}

      {(isApproved || isPaused) && (
        <>
          <div style={{ marginBottom: 16 }}>
            <ShareLinkCard
              shareLink={data.shareLink}
              refCode={a.refCode}
              commissionPct={a.commissionPct}
              cookieDays={a.cookieDays}
              theme={theme}
              toast={toast}
            />
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: 12,
            marginBottom: 16
          }}>
            <StatCard
              icon={TrendingUp}
              label="Clicks (30d)"
              value={data.stats?.clicks30d ?? 0}
              sub={`${a.aggregates.totalClicks} all-time`}
              theme={theme}
              accent="#3b82f6"
            />
            <StatCard
              icon={Users}
              label="Signups"
              value={a.aggregates.totalSignups}
              sub={`${a.aggregates.totalPayingCustomers} paying`}
              theme={theme}
              accent="#8b5cf6"
            />
            <StatCard
              icon={BadgePercent}
              label="Lifetime"
              value={formatEuros(a.aggregates.lifetimeCommissionCents)}
              sub={`at ${a.commissionPct}% commission`}
              theme={theme}
              accent="#10b981"
            />
            <StatCard
              icon={Wallet}
              label="Paid out"
              value={formatEuros(a.aggregates.paidOutCents)}
              sub={`${data.payouts.length} payouts`}
              theme={theme}
              accent="#f59e0b"
            />
          </div>

          <div style={{ marginBottom: 16 }}>
            <PayoutProgress
              pendingCents={a.aggregates.pendingPayoutCents}
              minPayoutCents={data.defaults?.minPayoutCents ?? 10000}
              theme={theme}
            />
          </div>

          {isApproved && (
            <div style={{ marginBottom: 16 }}>
              <ShareInviteCard
                stats={data.shareInviteStats}
                dailyLimit={data.defaults?.shareInvitesPerDay ?? 25}
                disabled={!isApproved}
                theme={theme}
                toast={toast}
                onSend={async (payload) => {
                  const result = await apiCallJson('/api/affiliate/share-invite', {
                    method: 'POST',
                    body: payload
                  });
                  await load();
                  return result;
                }}
              />
            </div>
          )}

          {(data.shareInvites && data.shareInvites.length > 0) && (
            <Section
              title="Sent invitations"
              theme={theme}
              right={
                <span style={{ color: theme.colors.text.tertiary, fontSize: 12 }}>
                  {data.shareInviteStats?.totalDelivered ?? 0} delivered · {data.shareInviteStats?.totalSent ?? 0} total
                </span>
              }
            >
              <ShareInvitesTable rows={data.shareInvites} theme={theme} />
            </Section>
          )}

          <Section title="Recent referrals" theme={theme}>
            <ReferralsTable rows={data.referrals} theme={theme} />
          </Section>

          <Section title="Commission ledger" theme={theme}>
            <CommissionsTable rows={data.commissions} theme={theme} />
          </Section>

          <Section title="Payout history" theme={theme}
            right={<a
              href="mailto:hello@bidoryx.com?subject=Affiliate%20payout%20question"
              style={{ color: theme.colors.text.tertiary, fontSize: 12, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4 }}
            >
              Need help? <ExternalLink size={12} />
            </a>}
          >
            <PayoutsTable rows={data.payouts} theme={theme} />
          </Section>
        </>
      )}
    </div>
  );
}
